import React, { useEffect, useRef, useState } from 'react';
import { notesService } from '../../services/notesService';
import { PatientNote } from '../../models/patientNote';
import { useSidePanel } from '../../contexts/SidePanelContext';
import { timeAgo } from '../insights/insightPresentation';
import { PinIcon } from '../../assets/icons';

// Compact strip showing the patient's most recent pinned note on the patient
// detail page. Deliberately unobtrusive: a single truncated line, renders
// nothing when there are no pinned notes. Clicking anywhere opens the notes
// panel on the "Fijadas" tab.

const MAX_VISIBLE = 1;

// Flatten note text to a single preview line (drop markdown markers).
const previewText = (text: string): string =>
  text
    .replace(/\*\*/g, '')
    .split('\n')
    .map((l) => l.replace(/^\s*-\s+/, '').trim())
    .filter(Boolean)
    .join(' · ');

interface PinnedNotesStripProps {
  patientId: number;
  patientName: string;
}

const PinnedNotesStrip: React.FC<PinnedNotesStripProps> = ({ patientId, patientName }) => {
  const sidePanel = useSidePanel();
  const [notes, setNotes] = useState<PatientNote[]>([]);
  const [count, setCount] = useState(0);
  const fetchSeqRef = useRef(0);

  const notesPanelOpen = sidePanel?.activePanel === 'notes';

  // Fetch on mount / patient change, and refetch when the notes panel closes
  // so pins made or removed there show up immediately.
  useEffect(() => {
    if (notesPanelOpen) return;
    const seq = ++fetchSeqRef.current;
    notesService
      .getNotes(patientId, { pinned: true, perpage: MAX_VISIBLE })
      .then((res) => {
        if (fetchSeqRef.current !== seq) return;
        setNotes(res.results);
        setCount(res.count);
      })
      .catch(() => {
        // Non-essential surface: fail silently and render nothing.
        if (fetchSeqRef.current !== seq) return;
        setNotes([]);
        setCount(0);
      });
  }, [patientId, notesPanelOpen]);

  if (notes.length === 0) return null;

  const openPanel = () =>
    sidePanel?.openNotesForPatient({ id: patientId, name: patientName }, 'pinned');
  const hiddenCount = count - notes.length;

  return (
    <div
      style={{
        border: '1px solid #ccfbf1',
        background: '#f0fdfa',
        borderRadius: 10,
        padding: '8px 12px',
        marginBottom: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ color: '#0f766e', display: 'flex', alignItems: 'center' }}>
          <PinIcon />
        </span>
        <span
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: 0.8,
            textTransform: 'uppercase',
            color: '#0f766e',
          }}
        >
          Notas fijadas
        </span>
        <button
          onClick={openPanel}
          style={{
            marginLeft: 'auto',
            border: 'none',
            background: 'transparent',
            color: '#0f766e',
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
            padding: '2px 4px',
          }}
        >
          Ver todas →
        </button>
      </div>
      {notes.map((note) => (
        <button
          key={note.id}
          onClick={openPanel}
          title={note.text}
          style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 8,
            border: 'none',
            background: 'transparent',
            padding: '3px 0',
            cursor: 'pointer',
            textAlign: 'left',
            width: '100%',
            minWidth: 0,
          }}
        >
          <span
            style={{
              flex: 1,
              minWidth: 0,
              fontSize: 13,
              color: '#134e4a',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {previewText(note.text)}
          </span>
          <span style={{ fontSize: 11, color: '#0f766e', opacity: 0.75, flexShrink: 0 }}>
            {note.author?.name ? `${note.author.name} · ` : ''}
            {timeAgo(note.createdAt)}
          </span>
        </button>
      ))}
      {hiddenCount > 0 && (
        <button
          onClick={openPanel}
          style={{
            border: 'none',
            background: 'transparent',
            color: '#0f766e',
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
            padding: '2px 0',
            textAlign: 'left',
          }}
        >
          +{hiddenCount} más
        </button>
      )}
    </div>
  );
};

export default PinnedNotesStrip;
